import notifee, { Trigger } from '@notifee/react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

export const LOGIN_PROMPT_NOTIFICATION_ID = 'login-prompt-daily';

const LEGACY_TRIGGERS_CLEARED_KEY = 'legacyLoginPromptTriggersCleared';

/**
 * One-time sweep for existing devices: pre-572ce01 builds scheduled the daily
 * login prompt via createTriggerNotification WITHOUT an id, so each schedule
 * produced a random-id repeating DAILY alarm. Those ghosts survive app updates
 * and keep firing/stacking because the current fixed-id cleanup can't reach
 * them. This one-time sweep cancels all pending triggers (safe at first run —
 * fixed-id login prompt + Getting Started nudges are rescheduled by their own
 * sync effects immediately after). New devices hit this at most once and see
 * no behavior change.
 */
export const clearLegacyLoginPromptTriggers = async () => {
  try {
    const alreadyCleared = await AsyncStorage.getItem(
      LEGACY_TRIGGERS_CLEARED_KEY,
    );
    if (alreadyCleared === 'true') return;
    await notifee.cancelTriggerNotifications();
    await AsyncStorage.setItem(LEGACY_TRIGGERS_CLEARED_KEY, 'true');
  } catch (error) {
    console.log('Error clearing legacy login prompt triggers:', error);
  }
};

export const scheduleBackgroundNotification = async ({
  id,
  title,
  body,
  trigger,
  data,
}: {
  id?: string;
  title: string;
  body: string;
  trigger: Trigger;
  data?: Record<string, string>;
}) => {
  try {
    const notificationId = await notifee.createTriggerNotification(
      {
        id,
        title,
        body,
        data,
        android: {
          channelId: 'default',
          importance: 4,
          vibrationPattern: [250, 250],
          pressAction: {
            id: 'default',
            launchActivity: 'default',
          },
        },
      },
      trigger,
    );
    return notificationId;
  } catch (error) {
    console.log('Error scheduling notification:', error);
  }
};

export const scheduleLoginPromptNotification = async (trigger: Trigger) => {
  const message = getRandomPromptLoginMessage();
  await notifee.cancelTriggerNotification(LOGIN_PROMPT_NOTIFICATION_ID);
  return scheduleBackgroundNotification({
    id: LOGIN_PROMPT_NOTIFICATION_ID,
    title: message.title,
    body: message.body,
    trigger,
  });
};

export const cancelLoginPromptNotification = async () => {
  try {
    await Promise.all([
      notifee.cancelTriggerNotification(LOGIN_PROMPT_NOTIFICATION_ID),
      notifee.cancelNotification(LOGIN_PROMPT_NOTIFICATION_ID),
    ]);
  } catch (error) {
    console.log('Error cancelling login prompt notification:', error);
  }
};

const messages = [
  {
    title: '💰 Your store is active — review sales now',
    body: 'Dont let another minute pass by, login now to review your sales figures.',
  },
];

export const getRandomPromptLoginMessage = () => {
  return messages[Math.floor(Math.random() * messages.length)];
};

export const isLoginPromptNotificationContent = ({
  title,
  body,
}: {
  title?: string | null;
  body?: string | null;
}) => {
  return messages.some(
    message => message.title === title && message.body === body,
  );
};
